<template>
  <div
    :class="{
      'catalog-slider-item-fi': true,
      'catalog-slider-item-fi--is-thumbnail': isThumbnail,
    }"
    @click="onZoom"
  >
    <video-player-fi
      v-if="item.videoId"
      ref="video"
      :fields="getYoutubeVideoFields(item.videoId, item.Url)"
      :show-hide-delay="1000"
      :reset-video="slideChanged"
      :aspect-ratio="16 / 9"
      :overlay-breakpoints="breakpoints"
      :unlimited="unlimited"
      :unlimited-height="unlimitedHeight"
      :small-play-button="isThumbnail"
      :is-thumbnail="isThumbnail"
      preview-params="func=crop&org_if_sml=0"
      do-not-replace-url
      no-youku-player
    />
    <div
      v-else
      class="catalog-slider-item-fi__image-container"
      data-test="catalog-slider-item-image-container"
    >
      <div
        v-if="zoomable"
        class="catalog-slider-item-fi__zoom fi-hidden md:fi-block"
      >
        <icon-fi icon="zoom-in" />
        {{ $dict.get('product-Catalog.Zoom') }}
      </div>
      <div
        :class="{
          'catalog-slider-item-fi__image-wrapper': true,
          'catalog-slider-item-fi__image-wrapper--product-picture':
            !isThumbnail && isProductPicture,
        }"
      >
        <ssr-cloud-image-fi
          class="thumbnail-slider-item-fi__image"
          :src="item.Url || undefined"
          :params="cloudImageParams"
          :trim="item.Type === 'Product Category Picture' || item.Type === 'Product Picture'"
          :breakpoints="breakpoints"
          :unlimited="unlimited"
          :unlimited-height="unlimitedHeight"
          :alt="
            isProductPicture && itemName
              ? `${item.altText}: &quot;${itemName}&quot;`
              : item.altText || undefined
          "
          :eager="eager"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconFi, SsrCloudImageFi } from 'atoms';
import { computed, useTemplateRef } from 'vue';

import VideoPlayerFi from '@/components/video-player-fi/VideoPlayerFi.vue';
import { useFischer } from '@/composables/fischerPlugin';

interface CatalogItem {
  Type?: string | null;
  Url?: string | null;
  videoId?: string | null;
  altText?: string | null;
}

interface Props {
  item?: CatalogItem;
  rootCategory?: string | null;
  isThumbnail?: boolean;
  slideChanged?: boolean;
  itemName?: string | null;
  zoomable?: boolean;
  isZoom?: boolean;
  eager?: boolean;
}

interface Emits {
  zoom: [];
}

const props = withDefaults(defineProps<Props>(), {
  item: () => ({}),
  rootCategory: null,
  isThumbnail: false,
  slideChanged: false,
  itemName: null,
  zoomable: false,
  isZoom: false,
  eager: false,
});

const emit = defineEmits<Emits>();

const fischer = useFischer();

const video = useTemplateRef<InstanceType<typeof VideoPlayerFi>>('video');
const isProductPicture = computed(() => {
  const type = props.item?.Type;
  if (!type) {
    return false;
  }
  const regularItemTypes = ['Product Category Picture', 'Product Picture', 'Packaging'].includes(
    type,
  );
  return fischer.app.isFite()
    ? regularItemTypes || type === 'Application Picture'
    : regularItemTypes || props.rootCategory === 'sf-root';
});

const cloudImageParams = computed(() => {
  return isProductPicture.value ? 'func=fit&bg_colour=fff&org_if_sml=0' : 'func=crop&org_if_sml=0';
});

const paddingMd = computed(() => {
  return isProductPicture.value ? 2 * 60 : 0;
});

const paddingLg = computed(() => {
  return isProductPicture.value ? 2 * 80 : 0;
});

const breakpoints = computed(() => {
  if (props.isThumbnail) {
    return [
      {
        mediaWidth: 480,
        imageWidth: 444 / 3,
        imageHeight: (444 / 3 / 16) * 9,
      },
      {
        mediaWidth: 767,
        imageWidth: 731 / 3,
        imageHeight: (731 / 3 / 16) * 9,
      },
      {
        mediaWidth: 768,
        imageWidth: 708 / 3,
        imageHeight: (708 / 3 / 16) * 9,
      },
      {
        mediaWidth: 1023,
        imageWidth: 963 / 3,
        imageHeight: (963 / 3 / 16) * 9,
      },
      {
        mediaWidth: 1440,
        imageWidth: 534 / 3,
        imageHeight: (534 / 3 / 16) * 9,
      },
    ];
  }
  if (props.isZoom) {
    return [
      {
        mediaWidth: 1440,
        imageWidth: 1380 - paddingLg.value,
        imageHeight: (1380 / 16) * 9,
      },
    ];
  }
  return [
    {
      mediaWidth: 480,
      imageWidth: 444,
      imageHeight: (444 / 16) * 9,
    },
    {
      mediaWidth: 767,
      imageWidth: 731,
      imageHeight: (731 / 16) * 9,
    },
    {
      mediaWidth: 768,
      imageWidth: 708,
      imageHeight: (708 / 16) * 9,
    },
    {
      mediaWidth: 1023,
      imageWidth: 963 - paddingMd.value,
      imageHeight: (963 / 16) * 9,
    },
    {
      mediaWidth: 1440,
      imageWidth: 534 - paddingLg.value,
      imageHeight: (534 / 16) * 9,
    },
  ];
});

const unlimited = computed(() => {
  if (props.isThumbnail) {
    return 798 / 3;
  }
  if (props.isZoom) {
    return 1860 - paddingLg.value;
  }
  return 798 - paddingLg.value;
});

const unlimitedHeight = computed(() => {
  if (props.isThumbnail) {
    return ((798 / 3) * 9) / 16;
  }
  if (props.isZoom) {
    return (1860 * 9) / 16;
  }
  return (798 * 9) / 16;
});
const onSwipe = (_index?: number) => {
  if (video.value?.isPlaying) {
    video.value.videoEnded();
  }
};

const onZoom = () => {
  if (props.zoomable) {
    emit('zoom');
  }
};

const getYoutubeVideoFields = (videoId: string, imageUrl: string | undefined | null) => {
  return {
    videoYoutubeLink: {
      value: {
        href: `https://www.youtube.com/watch?v=${videoId}`,
        url: `https://www.youtube.com/watch?v=${videoId}`,
        text: 'Youtube Video',
        altTitle: 'Youtube Video',
      },
    },
    image: {
      value: {
        src: imageUrl || '',
        alt: 'Youtube Video',
      },
    },
  };
};

defineExpose({
  onSwipe,
});
</script>

<style src="./catalog-slider-item-fi" />
